import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Ear, EarOff, Loader2, Send, Volume2, VolumeX, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { t } from "@/i18n";
import { isMuted, playNeigh, setMuted } from "@/lib/sounds";
import { HueyTip, useBounce, useHueyTip } from "@/components/HueyTip";
import { hueySpeak, isAutoRead, setAutoRead, stopHuey } from "@/lib/voice";
import { SpeakButton, useSpeaking } from "@/components/SpeakButton";
import { toast } from "sonner";

// Anonymous help-chat session id only (no personal data) — safe for localStorage.
const KEY = "huey-session";
const sessionId = () => {
  let id = localStorage.getItem(KEY);
  if (!id) { id = `h-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`; localStorage.setItem(KEY, id); }
  return id;
};
const STARTERS = ["huey_q_credits", "huey_q_colour", "huey_q_packets", "huey_q_safe"];

export const HueyHelper = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [replyAt, setReplyAt] = useState(0);
  const [muted, setMutedState] = useState(isMuted);
  const [autoRead, setAutoReadState] = useState(isAutoRead);
  const speakingId = useSpeaking();
  const endRef = useRef(null);
  const hidden = /^\/(colour\/|share\/|gallery\/)/.test(pathname);
  const [tip, dismissTip] = useHueyTip(pathname);
  const buttonBounce = useBounce(tip);
  const headerBounce = useBounce(replyAt);

  useEffect(() => {
    if (!open) return;
    api.get(`/huey/${sessionId()}`).then((r) => setMsgs(r.data.messages)).catch(() => {});
  }, [open]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  const toggleMute = () => { setMuted(!muted); setMutedState(!muted); if (!muted) stopHuey(); };
  const toggleAutoRead = () => {
    const next = !autoRead;
    setAutoRead(next); setAutoReadState(next);
    toast(t(next ? "huey_auto_read_on" : "huey_auto_read_off"));
    if (!next) stopHuey();
  };
  const closePanel = () => { stopHuey(); setOpen(false); };

  const send = async (text) => {
    const message = (text || input).trim();
    if (!message || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text: message }]);
    setBusy(true);
    try {
      const { data } = await api.post("/huey", { session_id: sessionId(), message });
      const at = Date.now();
      setMsgs((m) => [...m, { role: "huey", text: data.reply, at }]);
      setReplyAt(at);
      playNeigh();
      if (isAutoRead()) setTimeout(() => hueySpeak(`msg-${at}`, data.reply), 600);
    } catch (e) {
      setMsgs((m) => [...m, { role: "huey", text: formatApiError(e), error: true }]);
    } finally {
      setBusy(false);
    }
  };

  if (hidden) return null;
  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-3" data-testid="huey-helper" dir="ltr">
      {!open && <HueyTip tip={tip} onDismiss={dismissTip} onOpen={() => setOpen(true)} />}
      {open && (
        <div className="w-[min(92vw,380px)] h-[min(70vh,520px)] bg-white rounded-3xl border-4 border-[#1E293B] shadow-[6px_8px_0_0_#1E293B] flex flex-col overflow-hidden rise" data-testid="huey-panel">
          <div className="flex items-center gap-3 px-4 py-3 bg-[#FFD166] border-b-4 border-[#1E293B]">
            <img src="/huey.png" alt="Huey the Horse" className={`w-12 h-12 object-contain ${headerBounce ? "huey-bounce" : speakingId ? "huey-talk" : ""}`} data-testid="huey-header-avatar" />
            <div className="flex-1 min-w-0">
              <p className="font-display font-bold leading-tight">{t("huey_name")}</p>
              <p className="text-xs font-bold text-[#1E293B]/70">{t("huey_tagline")}</p>
            </div>
            <button onClick={toggleAutoRead} className={`p-2 rounded-xl hover:bg-white/60 ${autoRead ? "bg-white/70" : ""}`} aria-pressed={autoRead} aria-label={t("huey_auto_read")} title={t("huey_auto_read")} data-testid="huey-auto-read-button">
              {autoRead ? <Ear className="w-5 h-5" /> : <EarOff className="w-5 h-5 text-[#1E293B]/50" />}
            </button>
            <button onClick={toggleMute} className="p-2 rounded-xl hover:bg-white/60" aria-pressed={muted} aria-label={muted ? "Unmute Huey" : "Mute Huey"} data-testid="huey-mute-button">
              {muted ? <VolumeX className="w-5 h-5 text-[#1E293B]/50" /> : <Volume2 className="w-5 h-5" />}
            </button>
            <button onClick={closePanel} className="p-2 rounded-xl hover:bg-white/60" aria-label="Close" data-testid="huey-close-button"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-[#FFF9F2]" data-testid="huey-messages">
            <div className="flex gap-2 items-end">
              <img src="/huey.png" alt="" className="w-8 h-8 object-contain shrink-0" />
              <p className="bg-white border-2 border-slate-200 rounded-2xl rounded-bl-md px-3.5 py-2.5 text-sm max-w-[85%]">{t("huey_hello")}</p>
              <SpeakButton id="hello" text={t("huey_hello")} testid="huey-speak-hello" />
            </div>
            {msgs.length === 0 && (
              <div className="flex flex-wrap gap-2 pl-10" data-testid="huey-starters">
                {STARTERS.map((k) => (
                  <button key={k} onClick={() => send(t(k))} className="text-xs font-bold bg-white border-2 border-slate-200 hover:border-[#1E293B] rounded-full px-3 py-1.5 text-left" data-testid={`huey-starter-${k}`}>{t(k)}</button>
                ))}
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={`${m.at || "local"}-${i}`} className={`flex gap-2 items-end ${m.role === "user" ? "justify-end" : ""}`} data-testid={`huey-msg-${m.role}`}>
                {m.role !== "user" && <img src="/huey.png" alt="" className="w-8 h-8 object-contain shrink-0" />}
                <p className={`rounded-2xl px-3.5 py-2.5 text-sm max-w-[85%] whitespace-pre-wrap ${m.role === "user" ? "bg-[#1E293B] text-white rounded-br-md" : m.error ? "bg-red-50 border-2 border-red-200 text-red-700 rounded-bl-md" : "bg-white border-2 border-slate-200 rounded-bl-md"}`}>{m.text}</p>
                {m.role === "huey" && !m.error && <SpeakButton id={`msg-${m.at || i}`} text={m.text} testid={`huey-speak-msg-${i}`} />}
              </div>
            ))}
            {busy && <div className="flex gap-2 items-end"><img src="/huey.png" alt="" className="w-8 h-8 object-contain" /><p className="bg-white border-2 border-slate-200 rounded-2xl rounded-bl-md px-3.5 py-2.5"><Loader2 className="w-4 h-4 animate-spin" /></p></div>}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 p-3 border-t-2 border-slate-200 bg-white">
            <input value={input} onChange={(e) => setInput(e.target.value)} maxLength={600} placeholder={t("huey_placeholder")} data-testid="huey-input"
              className="flex-1 rounded-2xl border-2 border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-[#FF5252]" />
            <button type="submit" disabled={busy || !input.trim()} className="btn-pop btn-red !px-4 !py-2.5 disabled:opacity-50" aria-label="Send" data-testid="huey-send-button"><Send className="w-4 h-4" /></button>
          </form>
        </div>
      )}
      <button onClick={() => setOpen((o) => !o)} data-testid="huey-toggle-button" aria-label="Help from Huey" aria-expanded={open}
        className="group relative flex items-center gap-2 pl-2 pr-4 py-2 bg-white rounded-full border-4 border-[#1E293B] shadow-[4px_6px_0_0_#1E293B] hover:-translate-y-1 transition-transform">
        <img src="/huey.png" alt="Huey the Horse" className={`w-12 h-12 object-contain group-hover:-rotate-6 transition-transform ${buttonBounce ? "huey-bounce" : ""}`} data-testid="huey-button-avatar" />
        <span className="font-display font-bold text-sm hidden sm:inline">{open ? t("huey_close") : t("huey_ask")}</span>
      </button>
    </div>
  );
};
